// Feature: M001-F075 — Web 2SV bypass banner (M001-D020 §7.1, amended)
//
// This component used to own its own state: it called
// GET /v1/admin/2sv-overrides/count on a 60s poll and worked out whether the
// override system was switched on by running a regular expression over the prose
// of the 403 it got back. The admin page worked it out separately, from a
// different endpoint. They disagreed on screen.
//
// It now renders the shared status and holds no state of its own. Everything the
// banner claims comes from useTwoSvOverrideStatus(); the admin page reads the same
// value, so the two cannot contradict each other. Layout also mounts this
// component twice (mobile + sidebar) — with no local state, those two instances
// cannot drift apart either.
//
// DEFECT OF RECORD (2026-08-17, owner-reported): the banner rendered a permanent
// "⚠️ 2SV Bypass Disabled" warning — DISABLED is the system fully enforced, the
// safest state there is. A banner that is lit when nothing is wrong is not a
// warning, it is wallpaper, and it trains the reader to ignore the one moment it
// genuinely fires. §7.1/§7.1.1 are amended (same change) so the nav banner
// carries exactly one claim: "a bypass is in effect right now". DISABLED,
// UNAVAILABLE and PERMISSION are no longer rendered here — they still are, in
// full, on `/app/admin/2sv-overrides` (see TwoSvOverridesPage.js STATE_NOTICES),
// which remains the surface of record for "why is there no active-bypass
// warning". A hidden banner is therefore never a lie: it is compatible with
// enabled+0, disabled, unavailable, and permission-denied alike — none of those
// is "a bypass is active", which is the only thing this banner exists to say.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { OVERRIDE_STATE, useTwoSvOverrideStatus } from '../../context/TwoSvOverrideStatusContext';

/**
 * The banner's rendering contract, as a pure function of the shared status.
 * Returns null when nothing should be shown.
 *
 * This banner makes exactly one claim: "a 2SV bypass is in effect right now".
 * That is true only for ENABLED with a positive count. Every other state —
 * ENABLED with zero active overrides, DISABLED (no bypass CAN exist), and
 * UNAVAILABLE/PERMISSION (the state could not be read) — renders nothing here.
 * None of those states means "a bypass is active", so silence about them is not
 * a false claim; it is simply not this banner's claim to make. The admin page
 * is the surface of record for what the non-active state actually is.
 */
export const bannerLabelFor = ({ state, activeCount }) => {
  if (state === OVERRIDE_STATE.ENABLED) {
    return activeCount > 0 ? `⚠️ 2SV Bypass Active (${activeCount} active)` : null;
  }
  return null;
};

const TwoSvBypassBanner = ({ placement = 'sidebar' }) => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { state, activeCount } = useTwoSvOverrideStatus();

  if (!isAdmin()) return null;

  const label = bannerLabelFor({ state, activeCount });
  if (!label) return null;

  return (
    <button
      type="button"
      className={`two-sv-bypass-banner two-sv-bypass-banner--${placement === 'mobile' ? 'mobile' : 'sidebar'}`}
      data-testid="two-sv-bypass-banner"
      data-override-state={state}
      onClick={() => navigate('/app/admin/2sv-overrides')}
    >
      <span data-testid="two-sv-bypass-banner-label">{label}</span>
    </button>
  );
};

export default TwoSvBypassBanner;
